import {
  Button,
  Drawer,
  IconButton,
  Input,
  Typography,
  useMediaQuery,
  Paper,
  Box,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import planConfig from "../config/planConfig";
import { useState, useEffect } from "react";
import ProjectTypeSelector from "./ProjectTypeSelector";
import { Menu } from "@mui/icons-material";
import SubServiceSelector from "./SubServiceSelector";
import { toast } from "react-toastify";

// Project type data and sub-services from config
const projectTypes = planConfig.projectTypes;
const subServices = planConfig.subServices;

function Sidebar(props) {
  const {
    handleOptionChange,
    handleResetData,
    currentStep = 0,
    setCurrentStep,
    prName,
    setPrName,
    isEdit,
    setSelectedMainServiceKey,
    handleEdit,
    handleSave,
  } = props;
  const [selectedService, setSelectedService] = useState(null);
  const [activeSubServiceKey, setActiveSubServiceKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Initialize default service in step 4 (edit flow)
  useEffect(() => {
    if (currentStep === 4 && !selectedService) {
      const def = props.defaultServiceKey || projectTypes?.[0]?.key || null;
      if (def) setSelectedService(def);
    }
  }, [currentStep, selectedService, props.defaultServiceKey]);

  useEffect(() => {
    let isNew = localStorage.getItem("project_add_more");
    if (isNew) {
      setSelectedService(null);
    }
  }, [isEdit]);

  // When returning in edit, keep the service highlighted if provided from parent
  useEffect(() => {
    if (currentStep === 4 && props.selectedMainServiceKey) {
      setSelectedService(props.selectedMainServiceKey);
    }
  }, [currentStep, props.selectedMainServiceKey]);

  // Keep sub-service highlighted when coming from edit and geometry changes
  useEffect(() => {
    if (currentStep === 4 && props.selectedSubServiceKey) {
      setActiveSubServiceKey(props.selectedSubServiceKey);
    }
  }, [currentStep, props.selectedSubServiceKey]);

  const handleServiceSelect = (serviceKey, serviceLabel) => {
    setSelectedService(serviceKey);
    setSelectedMainServiceKey(null);
    setCurrentStep(4);
  };
  const handleContinue = async () => {
    setLoading(true);
    try {
      // call parent's save
      const latestData = props.handleInlineSave?.();
      setActiveSubServiceKey(null);

      let edit_id = localStorage.getItem("edit_id");

      if (edit_id) {
        handleEdit();
      }

      // wait 800ms so loader is visible
      await new Promise((res) => setTimeout(res, 800));

      // now trigger navigation
      await props.handleSubmitData?.(
        props.isEdit,
        latestData || props.projectData
      );
    } catch (err) {
      console.error("Error in handleContinue:", err);
      setLoading(false); // only reset if there was an error
    }
  };

  const handleSubServiceSelect = (subService) => {
    handleOptionChange({
      plan: subService.plan,
      mainServiceKey: selectedService,
      mainServiceLabel:
        projectTypes.find((t) => t.key === selectedService)?.label || null,
      subServiceKey: subService.key,
      subServiceLabel: subService.label,
    });
    setActiveSubServiceKey(subService.key);
  };

  const handleBackToServices = () => {
    setSelectedService(null);
    setCurrentStep(2);
  };

  // --- Step 0: Project Location ---
  if (currentStep === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          width: isMobile ? "100%" : 400,
          p: isMobile ? 6 : 3,
          height: "100%",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight="600" color="text.primary">
            Let's get started!
          </Typography>
          <IconButton
            onClick={() => {
              window.location.reload();
            }}
            size="small"
          >
            <RestartAltIcon />
          </IconButton>
        </Box>

        <Box mb={3}>
          <Typography
            variant="body1"
            fontWeight="500"
            color="text.primary"
            mb={1}
          >
            Where is the project located?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter a street address in the search bar above or find the project
            area in the map to the right.
          </Typography>
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => setCurrentStep(1)}
          sx={{ mt: 2, py: 1.5 }}
        >
          Continue
        </Button>
      </Paper>
    );
  }

  // --- Step 1: Project Name ---
  if (currentStep === 1) {
    return (
      <Paper
        elevation={3}
        sx={{
          width: isMobile ? "100%" : 400,
          p: isMobile ? 6 : 3,
          height: "100%",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight="600" color="text.primary">
            What is the name of this project?
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCurrentStep(0)}
          >
            Back
          </Button>
        </Box>

        <Input
          fullWidth
          value={prName}
          onChange={(e) => setPrName(e.target.value)}
          placeholder="Enter project name"
          sx={{
            mt: 2,
            mb: 3,
            p: 1.5,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            "&:focus-within": {
              borderColor: "primary.main",
              boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
            },
          }}
          disableUnderline
        />

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => {
            if (!prName) {
              toast.error("Project name is required", {
                toastId: "p_name_required",
              });
              return;
            }

            setCurrentStep(2);
          }}
          sx={{ py: 1.5 }}
        >
          Continue
        </Button>
      </Paper>
    );
  }

  // --- Step 2: Select Project Type ---
  if (currentStep === 2) {
    return (
      <ProjectTypeSelector
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        projectTypes={projectTypes}
        selectedService={selectedService}
        handleServiceSelect={handleServiceSelect}
      />
    );
  }

  // --- Step 4: Sub-services ---
  if (currentStep === 4 && selectedService) {
    const currentServiceData = projectTypes.find(
      (t) => t.key === selectedService
    );
    const availableSubServices = subServices[selectedService] || [];

    return (
      <SubServiceSelector
        availableSubServices={availableSubServices}
        currentServiceLabel={currentServiceData?.label}
        isMobile={isMobile}
        activeSubServiceKey={activeSubServiceKey}
        selectedSubServiceKey={props.selectedSubServiceKey}
        handleSubServiceSelect={handleSubServiceSelect}
        handleBackToServices={handleBackToServices}
        handleContinue={handleContinue}
        setCurrentStep={setCurrentStep}
        loading={loading}
        handleEdit={handleEdit}
        propsHandlers={{
          isSelectedSubService: props.isSelectedSubService,
          handleResetSubService: props.handleResetSubService,
          handleInlineSave: props.handleInlineSave,
          planHasGeometry: props.planHasGeometry,
          isActiveSubserviceDirty: props.isActiveSubserviceDirty,
          addKml: props.addKml,
        }}
      />
    );
  }

  return null;
}

function MobileSidebar(props) {
  const handleClose = () => {
    props.setOpenMenu(false);
  };

  return (
    <Drawer
      anchor="left"
      open={props.openMenu}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          left: "5px",
          top: 1,
          zIndex: "9999",
        }}
      >
        <Menu />
      </IconButton>
      <Sidebar {...props} isMobile={true} />
    </Drawer>
  );
}

export default function MapSideBar(props) {
  //check if mobile screen
  const isMobile = useMediaQuery("(max-width: 768px)");

  if (isMobile) {
    return <MobileSidebar {...props} />;
  }

  return (
    <div>
      <Sidebar {...props} />
    </div>
  );
}
